import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Server, Globe, Search, RefreshCw, Settings, CircleAlert as AlertCircle, ChevronLeft, ChevronRight, LayoutGrid, List } from 'lucide-react';
import { MobileSubMenuHeader } from '@/components/layout';
import { navigationItems } from '@/lib/navigation';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useXcloudConnection, useXcloudServers, useXcloudSites, useHostingPermission } from '@/hooks/useXcloud';
import { useQueryClient } from '@tanstack/react-query';
import { ServerCard } from '@/components/hosting/ServerCard';
import { SiteRow } from '@/components/hosting/SiteRow';
import { ServerDetailDrawer } from '@/components/hosting/ServerDetailDrawer';
import { SiteDetailDrawer } from '@/components/hosting/SiteDetailDrawer';
import type { XCloudServer, XCloudSite } from '@/types/xcloud';

interface HostingProps {
  isGlobalDarkMode: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

type MainTab = 'servers' | 'sites';

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-border bg-white dark:bg-card p-5 animate-pulse">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-muted" />
        <div className="space-y-2 flex-1">
          <div className="h-4 bg-muted rounded w-2/3" />
          <div className="h-3 bg-muted rounded w-1/2" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="h-3 bg-muted rounded" />
        <div className="h-3 bg-muted rounded" />
        <div className="h-3 bg-muted rounded" />
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-border bg-white dark:bg-card animate-pulse">
      <div className="w-8 h-8 rounded-lg bg-muted shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3.5 bg-muted rounded w-1/2" />
        <div className="h-3 bg-muted rounded w-1/3" />
      </div>
    </div>
  );
}

function NotConnectedState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-sky-500/10 flex items-center justify-center mb-5">
        <Server className="w-8 h-8 text-sky-500" />
      </div>
      <h2 className="text-xl font-bold text-foreground mb-2">xCloud Not Connected</h2>
      <p className="text-sm text-muted-foreground max-w-sm mb-6">
        Connect your xCloud account in Workspace Settings to view and manage your servers and sites.
      </p>
      <a
        href="?modal=settings&tab=workspace&workspaceTab=integrations"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium transition-colors"
      >
        <Settings className="w-4 h-4" />
        Go to Integrations Settings
      </a>
    </div>
  );
}

function InvalidTokenBanner() {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/8 border border-red-500/20 mb-6">
      <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-red-600 dark:text-red-400">xCloud token is invalid or expired</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Please reconnect your xCloud account in{' '}
          <a href="?modal=settings&tab=workspace&workspaceTab=integrations" className="underline hover:text-foreground">Workspace Settings &rarr; Integrations</a>.
        </p>
      </div>
    </div>
  );
}

function AccessRestrictedState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-5">
        <AlertCircle className="w-8 h-8 text-muted-foreground" />
      </div>
      <h2 className="text-xl font-bold text-foreground mb-2">Access Restricted</h2>
      <p className="text-sm text-muted-foreground max-w-sm">
        The Hosting section is only accessible to workspace owners, admins, and managers.
      </p>
    </div>
  );
}

const Hosting: React.FC<HostingProps> = ({ isGlobalDarkMode, onMobileSubMenuToggle, isMobile = false }) => {
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<MainTab>('servers');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedServer, setSelectedServer] = useState<XCloudServer | null>(null);
  const [selectedSite, setSelectedSite] = useState<XCloudSite | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [serverFilter, setServerFilter] = useState<string>('all');
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [serverPage, setServerPage] = useState(1);
  const [sitePage, setSitePage] = useState(1);
  const [serverView, setServerView] = useState<'grid' | 'list'>('grid');
  const [siteView, setSiteView] = useState<'list' | 'grid'>('list');

  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { canView, canManage } = useHostingPermission();
  const queryClient = useQueryClient();

  const { data: connection, isLoading: connectionLoading } = useXcloudConnection(workspaceId);
  const isConnected = connection?.status === 'connected';
  const isInvalid = connection?.status === 'invalid';

  const { data: serversData, isLoading: serversLoading, error: serversError } = useXcloudServers(
    isConnected ? workspaceId : undefined, serverPage
  );
  const { data: sitesData, isLoading: sitesLoading, error: sitesError } = useXcloudSites(
    isConnected ? workspaceId : undefined, sitePage
  );

  // Reset to page 1 when search/filter changes
  useEffect(() => { setServerPage(1); }, [searchQuery]);
  useEffect(() => { setSitePage(1); }, [searchQuery, serverFilter]);

  const handleMobileMenuToggle = () => {
    const crmMenuItem = navigationItems.find(item => item.id === 'crm');
    if (crmMenuItem && onMobileSubMenuToggle) {
      onMobileSubMenuToggle('crm', crmMenuItem.subItems, crmMenuItem.label);
    }
  };

  const handleRefresh = async () => {
    if (!workspaceId || isRefreshing) return;
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['xcloud', workspaceId] });
    setLastRefreshed(new Date());
    setIsRefreshing(false);
  };

  const servers = Array.isArray(serversData?.data) ? serversData.data : [];
  const serverMeta = serversData?.meta;
  const siteMeta = sitesData?.meta;

  const sites = useMemo(() => {
    const list = Array.isArray(sitesData?.data) ? sitesData.data : [];
    if (servers.length === 0) return list;

    return list.map(site => {
      if (site.server_name) return site;
      const server = servers.find(sv => 
        (site.server_uuid && sv.uuid === site.server_uuid) || 
        (site.server_id && String(sv.uuid) === String(site.server_id))
      );
      return {
        ...site,
        server_name: server?.name || site.server_name
      };
    });
  }, [sitesData?.data, servers]);

  const serverLastPage = serverMeta?.last_page ?? 1;
  const siteLastPage = siteMeta?.last_page ?? 1;
  const serverTotal = serverMeta?.total;
  const siteTotal = siteMeta?.total;

  const filteredServers = useMemo(() => {
    if (!searchQuery.trim()) return servers;
    const q = searchQuery.toLowerCase();
    return servers.filter(s =>
      s.name?.toLowerCase().includes(q) ||
      s.ip_address?.toLowerCase().includes(q) ||
      s.location?.toLowerCase().includes(q) ||
      s.region?.toLowerCase().includes(q) ||
      s.provider?.toLowerCase().includes(q)
    );
  }, [servers, searchQuery]);

  const filteredSites = useMemo(() => {
    let list = sites;
    if (serverFilter !== 'all') {
      list = list.filter(s => 
        s.server_uuid === serverFilter || 
        String(s.server_id) === serverFilter ||
        s.server_name === serverFilter
      );
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.title?.toLowerCase().includes(q) ||
        s.domain_name?.toLowerCase().includes(q) ||
        s.domain?.toLowerCase().includes(q) ||
        s.server_name?.toLowerCase().includes(q) ||
        s.php_version?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [sites, searchQuery, serverFilter]);

  if (!canView) {
    return (
      <div className="min-h-screen">
        <Helmet><title>Hosting | AgencyPlus</title></Helmet>
        {isMobile && (
          <MobileSubMenuHeader
            onMenuClick={handleMobileMenuToggle}
            onAddClick={() => {}}
            searchValue={mobileSearchQuery}
            onSearchChange={setMobileSearchQuery}
            searchPlaceholder="Search hosting..."
            showSearch={false}
            showAdd={false}
            isDarkMode={isGlobalDarkMode}
          />
        )}
        <div className={`w-full px-6 md:px-10 py-6 ${isMobile ? 'pt-20' : ''}`}>
          <AccessRestrictedState />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Hosting | AgencyPlus</title>
        <meta name="description" content="Manage servers and sites via xCloud" />
        <link rel="canonical" href="/sites/hosting" />
      </Helmet>

      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          onAddClick={() => {}}
          searchValue={mobileSearchQuery}
          onSearchChange={setMobileSearchQuery}
          searchPlaceholder="Search hosting..."
          showSearch={true}
          showAdd={false}
          isDarkMode={isGlobalDarkMode}
        />
      )}

      <div className={`w-full px-6 md:px-10 py-6 ${isMobile ? 'pt-20' : ''}`}>
        {/* Page header — hidden when viewing a detail page */}
        <div className={`flex items-start justify-between mb-6 gap-4 ${selectedServer || selectedSite ? 'hidden' : ''}`}>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Hosting</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Manage servers and sites via xCloud
            </p>
          </div>
          {isConnected && (
            <div className="flex items-center gap-2 shrink-0">
              {lastRefreshed && (
                <span className="text-xs text-muted-foreground hidden sm:block">
                  Updated {lastRefreshed.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border hover:bg-muted text-sm font-medium text-foreground transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          )}
        </div>

        {/* Server detail full page */}
        {selectedServer && workspaceId && (
          <ServerDetailDrawer
            server={selectedServer}
            workspaceId={workspaceId}
            canManage={canManage}
            onClose={() => setSelectedServer(null)}
          />
        )}

        {/* Site detail full page */}
        {selectedSite && workspaceId && (
          <SiteDetailDrawer
            site={selectedSite}
            workspaceId={workspaceId}
            canManage={canManage}
            onClose={() => setSelectedSite(null)}
          />
        )}

        {/* Loading connection state */}
        {!selectedServer && !selectedSite && connectionLoading && (
          null
        )}

        {/* Not connected */}
        {!selectedServer && !selectedSite && !connectionLoading && !isConnected && !isInvalid && (
          <NotConnectedState />
        )}

        {/* Connected (or invalid) */}
        {!selectedServer && !selectedSite && !connectionLoading && (isConnected || isInvalid) && (
          <>
            {isInvalid && <InvalidTokenBanner />}

            {isConnected && (
              <>
                {/* Tabs + Search bar */}
                <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                  <div className="flex items-center gap-1 p-1 rounded-lg bg-muted">
                    <button
                      onClick={() => setActiveTab('servers')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        activeTab === 'servers'
                          ? 'bg-white dark:bg-card text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Server className="w-4 h-4" />
                      Servers
                      {(serverTotal ?? servers.length) > 0 && (
                        <span className="ml-1 text-xs bg-sky-500/15 text-sky-600 dark:text-sky-400 px-1.5 py-0.5 rounded-full">
                          {serverTotal ?? servers.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('sites')}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        activeTab === 'sites'
                          ? 'bg-white dark:bg-card text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Globe className="w-4 h-4" />
                      Sites
                      {(siteTotal ?? sites.length) > 0 && (
                        <span className="ml-1 text-xs bg-sky-500/15 text-sky-600 dark:text-sky-400 px-1.5 py-0.5 rounded-full">
                          {siteTotal ?? sites.length}
                        </span>
                      )}
                    </button>
                  </div>

                  <div className="flex items-center gap-2 flex-1">
                    <div className="relative flex-1 max-w-sm">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder={activeTab === 'servers' ? 'Search servers...' : 'Search sites...'}
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-white dark:bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500"
                      />
                    </div>
                    {activeTab === 'sites' && servers.length > 0 && (
                      <select
                        value={serverFilter}
                        onChange={e => setServerFilter(e.target.value)}
                        className="px-3 py-2 rounded-lg border border-border bg-white dark:bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-sky-500/30"
                      >
                        <option value="all">All Servers</option>
                        {servers.map((s, i) => (
                          <option key={s.uuid || i} value={s.uuid}>{s.name}</option>
                        ))}
                      </select>
                    )}

                    {/* View toggle */}
                    <div className="flex items-center gap-0.5 p-0.5 rounded-lg border border-border bg-muted ml-auto">
                      {activeTab === 'servers' ? (
                        <>
                          <button
                            onClick={() => setServerView('grid')}
                            className={`p-1.5 rounded-md transition-colors ${serverView === 'grid' ? 'bg-white dark:bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            title="Grid view"
                          >
                            <LayoutGrid className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setServerView('list')}
                            className={`p-1.5 rounded-md transition-colors ${serverView === 'list' ? 'bg-white dark:bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            title="List view"
                          >
                            <List className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setSiteView('list')}
                            className={`p-1.5 rounded-md transition-colors ${siteView === 'list' ? 'bg-white dark:bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            title="List view"
                          >
                            <List className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setSiteView('grid')}
                            className={`p-1.5 rounded-md transition-colors ${siteView === 'grid' ? 'bg-white dark:bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            title="Grid view"
                          >
                            <LayoutGrid className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Servers Tab */}
                {activeTab === 'servers' && (
                  <>
                    {serversLoading ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[...Array(3)].map((_, i) => <SkeletonCard key={i} />)}
                      </div>
                    ) : serversError ? (
                      <ErrorState message={(serversError as Error).message} />
                    ) : filteredServers.length === 0 ? (
                      <EmptyResultState label={searchQuery ? 'No servers match your search' : 'No servers found'} />
                    ) : (
                      <>
                        {serverView === 'grid' ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredServers.map((server, i) => (
                              <ServerCard
                                key={server.uuid || i}
                                server={server}
                                view="grid"
                                onClick={() => setSelectedServer(server)}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            {filteredServers.map((server, i) => (
                              <ServerCard
                                key={server.uuid || i}
                                server={server}
                                view="list"
                                onClick={() => setSelectedServer(server)}
                              />
                            ))}
                          </div>
                        )}
                        {serverLastPage > 1 && (
                          <Pagination
                            page={serverPage}
                            lastPage={serverLastPage}
                            total={serverTotal}
                            onPrev={() => setServerPage(p => Math.max(1, p - 1))}
                            onNext={() => setServerPage(p => Math.min(serverLastPage, p + 1))}
                          />
                        )}
                      </>
                    )}
                  </>
                )}

                {/* Sites Tab */}
                {activeTab === 'sites' && (
                  <>
                    {sitesLoading ? (
                      <div className="flex flex-col gap-2">
                        {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
                      </div>
                    ) : sitesError ? (
                      <ErrorState message={(sitesError as Error).message} />
                    ) : filteredSites.length === 0 ? (
                      <EmptyResultState label={searchQuery ? 'No sites match your search' : 'No sites found'} />
                    ) : (
                      <>
                        {siteView === 'grid' ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredSites.map((site, i) => (
                              <SiteRow
                                key={site.uuid || i}
                                site={site}
                                view="grid"
                                onClick={() => setSelectedSite(site)}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            {filteredSites.map((site, i) => (
                              <SiteRow
                                key={site.uuid || i}
                                site={site}
                                view="list"
                                onClick={() => setSelectedSite(site)}
                              />
                            ))}
                          </div>
                        )}
                        {siteLastPage > 1 && (
                          <Pagination
                            page={sitePage}
                            lastPage={siteLastPage}
                            total={siteTotal}
                            onPrev={() => setSitePage(p => Math.max(1, p - 1))}
                            onNext={() => setSitePage(p => Math.min(siteLastPage, p + 1))}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

    </div>
  );
};

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
        <AlertCircle className="w-6 h-6 text-red-500" />
      </div>
      <p className="text-sm font-medium text-foreground mb-1">Failed to load data</p>
      <p className="text-xs text-muted-foreground max-w-xs">{message}</p>
    </div>
  );
}

function EmptyResultState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function Pagination({ page, lastPage, total, onPrev, onNext }: {
  page: number;
  lastPage: number;
  total?: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center justify-between mt-4 px-1">
      <p className="text-xs text-muted-foreground">
        {total != null ? `${total} total · ` : ''}Page {page} of {lastPage}
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={onPrev}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </button>
        <button
          onClick={onNext}
          disabled={page >= lastPage}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default Hosting;
